import * as React from "react";
import { useState } from "react";

export interface SelectOption {
  id: string;
  name: string;
}

interface SelectProps {
  options: SelectOption[];
  selected: SelectOption | null;
  emptyLabel: string;
  onChange: (option: SelectOption) => void;
  labeledBy: string;
  id: string;
}

// Same props/behavior contract as the custom Select, but backed by a real
// <select> element. This trades pixel-perfect control over the open
// dropdown (the popup is rendered by the OS/browser and can't be styled
// with CSS) for free native screen reader and keyboard support on every
// platform — VoiceOver, NVDA, JAWS, TalkBack, mobile pickers, etc. all
// already know how to operate a <select> correctly.
export default function NativeSelect({
  options,
  selected,
  emptyLabel,
  onChange,
  labeledBy,
  id,
}: SelectProps) {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const option = options.find((o) => o.id === e.target.value);
    if (option) {
      onChange(option);
    }
  };

  return (
    <div
      style={{
        ...styles.container,
        ...(isFocused ? styles.containerFocused : {}),
      }}
    >
      <select
        id={id}
        aria-labelledby={labeledBy}
        value={selected ? selected.id : ""}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        style={{
          ...styles.select,
          ...(selected ? styles.value : styles.placeholder),
        }}
      >
        {/* Hidden once a real option is chosen, same as the custom
            version's emptyLabel placeholder text */}
        {!selected && (
          <option value="" disabled hidden>
            {emptyLabel}
          </option>
        )}
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>

      {/* The native arrow is suppressed via appearance: none on the
          <select>, and this SVG stands in so the closed state matches the
          custom Select's chevron. It's pointer-events: none so clicks pass
          through to the select underneath. */}
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        style={styles.chevron}
      >
        <path
          d="M4 6l4 4 4-4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

/* ─── Inline styles ──────────────────────────────────────── */

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: "relative",
    width: "100%",
    borderRadius: 6,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
  },
  containerFocused: {
    outline: "2px solid #2563eb",
    outlineOffset: 2,
  },
  select: {
    width: "100%",
    margin: 0,
    padding: "8px 32px 8px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 6,
    backgroundColor: "#fff",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit",
    lineHeight: "normal",
    // Strip native OS chrome (arrow, inset shadows, etc.) so the custom
    // chevron + border styling above is what's visible.
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    outline: "none",
  },
  value: {
    color: "#111827",
  },
  placeholder: {
    color: "#000000",
  },
  chevron: {
    position: "absolute",
    top: "50%",
    right: 12,
    transform: "translateY(-50%)",
    color: "#6b7280",
    pointerEvents: "none",
  },
};
